import { Box, Button, Card, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import logo from "../assets/logo.png";
import Footer from "../components/shared/Footer";
import LoadingIndicator from "../components/shared/LoadingIndicator";
import { useAuth } from "../contexts/AuthContext";
import { usePostHog } from "../hooks/usePostHog";

interface ConsentInfo {
    consent_required: boolean;
    redirect_host: string | null;
    client: string | null;
}

/**
 * ConsentPage ("/approve")
 *
 * The explicit "do you want to connect this app?" decision. Being signed in
 * identifies the user; it is not approval to connect an unrecognised app. For a
 * destination that could hand the authorization code to a third party, the
 * server withholds the code and routes the browser here. Allow approves this one
 * request and completes; Cancel abandons it. Approval is not remembered — the
 * next connection to the same destination is prompted again.
 *
 * First-party assistants (ChatGPT, Claude) and own-machine destinations are
 * auto-approved server-side and never reach this page.
 */
export default function ConsentPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const { user, isSessionChecked } = useAuth();
    const { track } = usePostHog();

    const [info, setInfo] = useState<ConsentInfo | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const navigatedAway = useRef(false);

    // Guard against "DoubleClickjacking": an attacker swaps this page under a
    // double-click so the second click lands on Allow. X-Frame-Options stops
    // framing but not a window swap. So Allow is enabled only after the page has
    // been continuously visible and focused for ~500ms, and is disabled again on
    // blur — a click arriving right after a swap hits a dead button.
    const [armed, setArmed] = useState(false);
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const disarm = () => {
            setArmed(false);
            if (timer) {
                clearTimeout(timer);
                timer = undefined;
            }
        };
        const arm = () => {
            if (timer || document.visibilityState !== "visible" || !document.hasFocus()) {
                return;
            }
            timer = setTimeout(() => {
                setArmed(true);
                timer = undefined;
            }, 500);
        };
        const onVisibility = () => (document.visibilityState === "visible" ? arm() : disarm());
        arm();
        window.addEventListener("focus", arm);
        window.addEventListener("blur", disarm);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            window.removeEventListener("focus", arm);
            window.removeEventListener("blur", disarm);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const goToLogin = useCallback(() => {
        const authOrigin = import.meta.env.VITE_AUTH_URL || window.location.origin;
        const params = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : "";
        window.location.href = `${authOrigin}/${params}`;
    }, [
        sessionId,
    ]);

    // Complete the session, optionally granting consent, then follow the connector redirect.
    const complete = useCallback(
        async (grantConsent: boolean) => {
            if (!sessionId || navigatedAway.current) {
                return;
            }
            setSubmitting(true);
            try {
                const res = await fetch(`/auth/session/${encodeURIComponent(sessionId)}/complete`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(
                        grantConsent
                            ? {
                                  grant_consent: true,
                              }
                            : {},
                    ),
                });
                if (res.status === 401) {
                    goToLogin();
                    return;
                }
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data?.redirect_uri) {
                    throw new Error(data?.error_description || "Failed to complete authentication");
                }
                navigatedAway.current = true;
                window.location.href = data.redirect_uri;
            } catch (err) {
                setSubmitting(false);
                setLoadError(err instanceof Error ? err.message : "Failed to complete authentication");
            }
        },
        [
            sessionId,
            goToLogin,
        ],
    );

    // Load who is asking for access.
    useEffect(() => {
        if (!isSessionChecked) {
            return;
        }
        if (!sessionId) {
            setLoadError("This approval link is missing its session. Start the connection again from the app.");
            return;
        }
        if (!user) {
            goToLogin();
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/auth/session/${encodeURIComponent(sessionId)}/consent-info`, {
                    credentials: "include",
                });
                if (res.status === 401) {
                    goToLogin();
                    return;
                }
                const data = (await res.json().catch(() => ({}))) as Partial<ConsentInfo> & {
                    error_description?: string;
                };
                if (cancelled) {
                    return;
                }
                if (!res.ok) {
                    throw new Error(data?.error_description || "This connection request is no longer valid.");
                }
                // Already approved (or auto-approved): nothing to decide — just finish.
                if (!data.consent_required) {
                    void complete(false);
                    return;
                }
                setInfo({
                    consent_required: true,
                    redirect_host: data.redirect_host ?? null,
                    client: data.client ?? null,
                });
                track("oauth_consent_prompt_shown", {
                    redirect_host: data.redirect_host ?? "unknown",
                });
            } catch (err) {
                if (!cancelled) {
                    setLoadError(err instanceof Error ? err.message : "This connection request is no longer valid.");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        isSessionChecked,
        sessionId,
        user,
        goToLogin,
        complete,
        track,
    ]);

    const onAllow = () => {
        track("oauth_consent_allowed", {
            redirect_host: info?.redirect_host ?? "unknown",
        });
        void complete(true);
    };

    const onCancel = () => {
        track("oauth_consent_cancelled", {
            redirect_host: info?.redirect_host ?? "unknown",
        });
        const dashboardUrl = import.meta.env.VITE_MCP_URL || "/";
        window.location.href = dashboardUrl;
    };

    const appName = info?.client || info?.redirect_host || "An application";

    return (
        <Container
            size="2"
            p="4"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingBottom: "12vh",
            }}
        >
            <img
                src={logo}
                alt="Desktop Commander"
                style={{
                    width: 52,
                    height: 52,
                    objectFit: "contain",
                    marginBottom: 18,
                }}
            />
            <Card
                size="4"
                style={{
                    width: "100%",
                    maxWidth: 480,
                }}
            >
                {loadError ? (
                    <Flex
                        direction="column"
                        gap="4"
                        align="center"
                        style={{
                            textAlign: "center",
                        }}
                    >
                        <Heading size="5">Can't complete this connection</Heading>
                        <Text color="gray" size="2">
                            {loadError}
                        </Text>
                    </Flex>
                ) : !info ? (
                    <Flex
                        direction="column"
                        gap="3"
                        align="center"
                        style={{
                            padding: "24px 0",
                        }}
                    >
                        <LoadingIndicator />
                        <Text color="gray" size="2">
                            Loading…
                        </Text>
                    </Flex>
                ) : (
                    <Flex direction="column" gap="4">
                        <Heading size="5">Connect an app</Heading>

                        <Text size="3">
                            <strong>{appName}</strong> wants to connect to your Desktop Commander account. If you connect it, it can act on your
                            behalf and reach the files and commands on your connected devices.
                        </Text>

                        {info.redirect_host && (
                            <Box
                                style={{
                                    background: "var(--gray-a3)",
                                    borderRadius: "var(--radius-3)",
                                    padding: "10px 14px",
                                }}
                            >
                                <Text size="1" color="gray">
                                    Access will be sent to
                                </Text>
                                <Text
                                    size="2"
                                    style={{
                                        display: "block",
                                        wordBreak: "break-all",
                                    }}
                                >
                                    {info.redirect_host}
                                </Text>
                            </Box>
                        )}

                        <Text size="2" color="gray">
                            Only allow this if you started connecting {appName} yourself. If this appeared unexpectedly, choose Cancel.
                        </Text>

                        <Flex gap="3" mt="2" justify="end">
                            <Button variant="soft" color="gray" onClick={onCancel} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button onClick={onAllow} loading={submitting} disabled={!armed || submitting}>
                                Allow
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </Card>
            <Footer border={false} />
        </Container>
    );
}
